:root {
  --page: #d9e3e8;
  --ink: #102227;
  --mute: #5b6b72;
  --well: #14343c;
  --well-text: #e7f1f3;
  --well-mute: #9bb4b8;
  --well-line: #2c555c;
  --panel: #f7fafb;
  --you: #f0a07a;
  --mkt: #7ed4c9;
  --mkt-deep: #1f6f68;
  --pad: #efe6d6;
  --ui: "Plus Jakarta Sans", "Segoe UI", sans-serif;
  --display: "Fraunces", Georgia, serif;
  --mono: "IBM Plex Mono", ui-monospace, monospace;
  --col: 26.75rem;
  --shell: 88rem;
  --radius: 22px;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

html,
body,
#root {
  min-height: 100%;
}

body {
  margin: 0;
  background: var(--page);
  color: var(--ink);
  font-family: var(--ui);
  font-size: 1.02rem;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}

button,
input,
select,
textarea {
  font: inherit;
  color: inherit;
}

button {
  cursor: pointer;
}

a {
  color: var(--mkt-deep);
}

:focus-visible {
  outline: 2px solid var(--mkt);
  outline-offset: 3px;
}

.skip {
  position: absolute;
  left: 0.75rem;
  top: -3rem;
  z-index: 20;
  background: var(--well);
  color: var(--well-text);
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
}

.skip:focus {
  top: 0.75rem;
}

.app {
  min-height: 100dvh;
}

.top {
  position: sticky;
  top: 0;
  z-index: 5;
  background: var(--page);
  border-bottom: 1px solid #c5d2d8;
}

.top-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(var(--shell), calc(100% - 2.3rem));
  margin: 0 auto;
  padding: calc(0.85rem + env(safe-area-inset-top)) 0 0.85rem;
}

.brand {
  color: inherit;
  text-decoration: none;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
}

.brand small {
  display: block;
  color: var(--mute);
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0;
}

.top-links {
  display: flex;
  gap: 0.9rem;
}

.top span,
.top a {
  color: var(--mute);
  font-size: 0.875rem;
}

.shell {
  width: min(var(--shell), calc(100% - 2.3rem));
  margin: 0 auto;
  padding: 0 0 calc(6.4rem + env(safe-area-inset-bottom));
}

.mast {
  display: grid;
  gap: 1.15rem;
  padding: 1.45rem 0 1.7rem;
}

.intel {
  margin: 0 0 0.75rem;
}

.intel > summary {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.65rem;
  min-height: 36px;
  color: var(--mute);
  font-size: 0.8rem;
  cursor: pointer;
}

.intel > summary::-webkit-details-marker {
  display: none;
}

.intel > summary::after {
  content: "+";
  color: var(--mkt-deep);
  font-weight: 700;
}

.intel[open] > summary::after {
  content: "–";
}

.intel p {
  margin: 0.45rem 0 0;
  max-width: 36rem;
  color: var(--mute);
  font-size: 0.82rem;
}

.sample {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.65rem;
  margin: 0 0 0.55rem;
  padding: 0;
  color: var(--mute);
  font-size: 0.78rem;
}

.sample button {
  min-height: 44px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  box-shadow: inset 0 0 0 1px #c5d0d5;
  color: var(--ink);
  padding: 0.35rem 0.85rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.duel {
  display: grid;
  gap: 0.85rem;
}

.duel-lanes {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0.55rem;
  align-items: center;
}

.duel-lane {
  margin: 0;
  min-width: 0;
  padding: 0.55rem 0.7rem 0.6rem;
  border-radius: 16px;
  background: #10282e;
}

.duel-lane.is-you {
  background: color-mix(in srgb, var(--you) 16%, #10282e);
  box-shadow: inset 4px 0 0 var(--you);
}

.duel-lane.is-them {
  background: color-mix(in srgb, var(--mkt) 16%, #10282e);
  box-shadow: inset 4px 0 0 var(--mkt);
}

.duel-lane span,
.duel-lane small,
.duel-lane em {
  display: block;
}

.duel-lane span {
  color: var(--well-mute);
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.duel-lane strong {
  display: flex;
  align-items: baseline;
  margin: 0.15rem 0 0.2rem;
  font-family: var(--display);
  font-size: clamp(2rem, 7vw, 2.7rem);
  font-weight: 500;
  letter-spacing: -0.045em;
  line-height: 0.95;
}

.duel-lane.is-you strong {
  color: var(--you);
}

.duel-lane.is-them strong {
  color: var(--mkt);
}

.duel-lane small,
.duel-lane em {
  color: var(--well-mute);
  font-size: 0.8rem;
  font-style: normal;
}

.duel-pad {
  display: flex;
  gap: 0.3rem;
  margin: 0.3rem 0 0.2rem;
}

.duel-pad button {
  width: 1.7rem;
  height: 1.7rem;
  padding: 0;
  border: 1px solid var(--well-line);
  border-radius: 8px;
  background: #10282e;
  color: var(--well-text);
  font-family: var(--mono);
  font-size: 0.95rem;
  line-height: 1;
}

.duel-lane.is-you .duel-pad button:hover,
.duel-lane.is-you .duel-pad button:focus-visible {
  border-color: var(--you);
  color: var(--you);
}

.duel-lane.is-them .duel-pad button:hover,
.duel-lane.is-them .duel-pad button:focus-visible {
  border-color: var(--mkt);
  color: var(--mkt);
}

.duel-call {
  margin: 0;
  color: var(--well-text);
  font-size: 0.88rem;
  line-height: 1.4;
}

.duel-vs {
  margin: 0;
  color: var(--well-mute);
  font-family: var(--mono);
  font-size: 0.72rem;
  letter-spacing: 0.12em;
}

.duel-tug {
  display: flex;
  height: 12px;
  overflow: hidden;
  border-radius: 99px;
  background: var(--well-line);
}

.duel-tug i {
  display: block;
  height: 100%;
}

.duel-tug .is-you {
  background: var(--you);
}

.duel-tug .is-them {
  flex: 1;
  background: var(--mkt);
}

.cluster {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.45rem;
}

.cluster p {
  margin: 0;
  padding: 0.45rem 0.4rem;
  background: #10282e;
  border-radius: 12px;
}

.cluster span {
  display: block;
  color: var(--well-mute);
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.cluster b {
  display: block;
  margin-top: 0.15rem;
  font-family: var(--mono);
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--well-text);
}

.duel-hint {
  margin: 0.55rem 0 0;
  color: var(--well-mute);
  font-size: 0.78rem;
}

.rival-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem;
}

.rival-switch button {
  min-height: 64px;
  text-align: left;
  background: #10282e;
  border: 1px solid var(--well-line);
  border-radius: 16px;
  padding: 0.55rem 0.7rem;
  color: var(--well-mute);
  font-weight: 700;
}

.rival-switch button small {
  display: block;
  margin-top: 0.2rem;
  font-weight: 500;
  font-size: 0.72rem;
  line-height: 1.3;
  color: var(--well-mute);
}

.rival-switch .is-print[aria-selected="true"] {
  border-color: var(--mkt);
  background: #123f44;
  color: var(--mkt);
  box-shadow: inset 4px 0 0 var(--mkt);
}

.rival-switch .is-offer[aria-selected="true"] {
  border-color: var(--you);
  background: #2a221c;
  color: var(--you);
  box-shadow: inset 4px 0 0 var(--you);
}

.rival-offer {
  display: grid;
  gap: 0.35rem;
  margin: 0.15rem 0 0;
  padding: 0.75rem 0.85rem 0.8rem;
  background: #0e272d;
  border: 1px solid var(--you);
  border-radius: 18px;
}

.rival-offer > span:first-child {
  color: var(--you);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.rival-offer-box {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 56px;
  padding: 0 0.85rem;
  background: #10282e;
  border: 1px solid var(--well-line);
  border-radius: 14px;
}

.rival-offer input {
  flex: 1;
  min-width: 0;
  min-height: 52px;
  border: 0;
  background: transparent;
  color: var(--well-text);
  font-family: var(--display);
  font-size: 1.7rem;
  font-weight: 560;
  letter-spacing: -0.03em;
  outline: none;
}

.rival-offer input::placeholder {
  color: #6f888c;
}

.rival-offer-box:focus-within {
  border-color: var(--you);
}

.rival-offer em {
  color: var(--you);
  font-family: var(--display);
  font-size: 1.35rem;
  font-style: normal;
  font-weight: 560;
}

.rival-offer small {
  color: var(--well-mute);
  font-size: 0.75rem;
  line-height: 1.35;
}

.rate-stave {
  margin: 0;
  padding: 0.85rem 0.9rem 0.95rem;
  background: #0e272d;
  border: 1px solid var(--well-line);
  border-radius: 18px;
}

.rate-stave figcaption {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0 0 0.7rem;
  color: var(--mkt);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.rate-stave-axis {
  display: flex;
  gap: 0.85rem;
  color: var(--well-mute);
  font-family: var(--mono);
  font-size: 0.64rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: lowercase;
}

.rate-stave-rows {
  display: grid;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rate-stave-rows > li.is-you,
.rate-stave-rows button {
  display: grid;
  grid-template-columns: minmax(5.4rem, 7.2rem) minmax(0, 1fr) 4.4rem;
  align-items: center;
  gap: 0.55rem 0.65rem;
  width: 100%;
  min-height: 48px;
  margin: 0;
  padding: 0.4rem 0.55rem;
  border: 0;
  border-radius: 14px;
  background: #10282e;
  color: var(--well-text);
  text-align: left;
}

.rate-stave-rows > li.is-you {
  background: color-mix(in srgb, var(--you) 16%, #10282e);
  box-shadow: inset 4px 0 0 var(--you);
}

.rate-stave-rows > li.is-on button {
  background: color-mix(in srgb, var(--mkt) 16%, #10282e);
  box-shadow: inset 4px 0 0 var(--mkt);
}

.rate-stave-who {
  display: grid;
  min-width: 0;
}

.rate-stave-who b,
.rate-stave-who small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rate-stave-who b {
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.2;
}

.rate-stave-who small {
  color: var(--well-mute);
  font-size: 0.68rem;
  font-weight: 500;
}

.rate-stave-rows > li.is-you .rate-stave-who b,
.rate-stave-rows > li.is-you strong {
  color: var(--you);
}

.rate-stave-rows > li.is-on .rate-stave-who b,
.rate-stave-rows > li.is-on strong {
  color: var(--mkt);
}

.rate-stave-rows strong {
  font-family: var(--mono);
  font-size: 0.84rem;
  font-weight: 500;
  text-align: right;
  color: var(--well-text);
}

.rate-stave-lane {
  position: relative;
  min-width: 0;
  height: 18px;
  overflow: visible;
  background: #0a1c20;
  border-radius: 99px;
  box-shadow: inset 0 0 0 1px #071416;
}

.rate-stave-fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
}

.rate-stave-fill.is-you {
  background: var(--you);
}

.rate-stave-fill.is-mkt {
  background: color-mix(in srgb, var(--mkt) 72%, #0a1c20);
}

.rate-stave-fill.is-on {
  background: var(--mkt);
}

.rate-stave-you-mark {
  position: absolute;
  top: -5px;
  bottom: -5px;
  width: 0;
  border-left: 1px dashed var(--you);
  opacity: 0.55;
  transform: translateX(-50%);
  pointer-events: none;
}

.rate-stave-rows button {
  min-width: 0;
}

.rate-stave-rows button:hover,
.rate-stave-rows button:focus-visible {
  background: #14343c;
}

.rate-stave-peg {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.rate-stave-peg.is-you {
  width: 13px;
  height: 13px;
  background: var(--you);
  border-radius: 3px;
  box-shadow: 0 0 0 3px #0e272d;
}

.rate-stave-peg.is-mkt {
  background: var(--mkt);
  border-radius: 99px;
  box-shadow: 0 0 0 3px #0e272d;
}

.rate-stave-peg.is-on {
  width: 16px;
  height: 16px;
  background: #0e272d;
  border-radius: 99px;
  box-shadow: 0 0 0 3px var(--mkt);
}

@media (max-width: 640px) {
  .cluster {
    grid-template-columns: 1fr 1fr;
  }
}

.live {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--well);
  color: var(--mkt);
  border-radius: 999px;
  padding: 0.22rem 0.65rem 0.22rem 0.5rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.live::before {
  content: "";
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--mkt);
}

.live.is-snap {
  color: var(--well-mute);
}

.live.is-snap::before {
  background: var(--well-mute);
}

.mast h1 {
  margin: 0;
  font-family: var(--display);
  font-size: clamp(2.35rem, 11vw, 3.35rem);
  font-weight: 500;
  letter-spacing: -0.045em;
  line-height: 0.95;
}

.mast-eyebrow {
  display: block;
  margin-bottom: 0.45rem;
  font-family: var(--ui);
  font-size: 0.78rem;
  font-weight: 700;
  font-style: normal;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mkt-deep);
}

.mast h1 em {
  display: block;
  font-style: italic;
  color: var(--mkt-deep);
}

.mast-lead {
  margin: 0.85rem 0 0;
  max-width: 34rem;
  color: var(--mute);
  font-size: 1.02rem;
}

.match {
  background: var(--well);
  color: var(--well-text);
  border-radius: var(--radius);
  padding: 1rem 1.1rem 1.05rem;
}

.match-kicker {
  margin: 0 0 0.55rem;
  color: var(--mkt);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.match-beats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.match-beats button {
  display: grid;
  width: 100%;
  min-height: 52px;
  padding: 0.4rem 0.35rem;
  border: 1px solid var(--well-line);
  border-radius: 14px;
  background: transparent;
  color: var(--well-mute);
  font-size: 0.72rem;
  font-weight: 700;
  place-items: center;
}

.match-beats button span {
  display: block;
  font-family: var(--mono);
  font-size: 0.68rem;
  font-weight: 500;
}

.match-beats li.is-now button {
  background: var(--you);
  border-color: transparent;
  color: var(--ink);
  animation: beat-live 1.8s ease-in-out infinite;
}

@keyframes beat-live {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgb(240 160 122 / 0.4);
  }
  50% {
    box-shadow: 0 0 0 7px rgb(240 160 122 / 0);
  }
}

.match-beats li.is-done button {
  color: var(--mkt);
  border-color: #2f6d68;
}

.match-beats button:disabled {
  opacity: 0.45;
  cursor: default;
}

.match-keys {
  display: none;
}

.match-keys kbd {
  font-family: var(--mono);
  padding: 0.04rem 0.32rem;
  border: 1px solid var(--well-line);
  border-radius: 6px;
  color: var(--mkt);
}

.match-next {
  margin: 0.7rem 0 0;
  color: var(--well-mute);
  font-size: 0.82rem;
}

@media (min-width: 980px) {
  .match-keys {
    display: flex;
    gap: 0.7rem;
    margin: 0.55rem 0 0;
    color: var(--well-mute);
    font-size: 0.68rem;
    letter-spacing: 0.04em;
  }
}

html.is-live .seo-foot {
  display: none;
}

.seo-foot {
  padding: 2rem 0 3rem;
  color: var(--mute);
  font-size: 0.92rem;
}

.seo-foot h1 {
  font-family: var(--display);
  font-size: 1.5rem;
  font-weight: 500;
}

.seo-fold {
  margin-top: 1.5rem;
}

.seo-fold > summary {
  list-style: none;
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  min-height: 48px;
  cursor: pointer;
  border-bottom: 1px solid #c9d6dc;
}

.seo-fold > summary::-webkit-details-marker {
  display: none;
}

.seo-fold > summary::after {
  content: "+";
  margin-left: auto;
  color: var(--mkt-deep);
  font-size: 1.15rem;
  font-weight: 600;
}

.seo-fold[open] > summary::after {
  content: "–";
}

.promises {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.promises li {
  background: var(--panel);
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.stage {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stage-main,
.stage-side,
.stage-end,
.stage-rail {
  display: contents;
}

.loan-fold {
  order: 1;
  margin: 0;
  background: var(--panel);
  border-radius: var(--radius);
  padding: 0.15rem 1.35rem 1.45rem;
  box-shadow: inset 4px 0 0 var(--you);
}

.well,
.well-empty {
  order: 2;
}

.products-panel {
  order: 3;
}

.next {
  order: 4;
}

.source-fold {
  order: 5;
}

.loan-fold summary {
  min-height: 52px;
  display: flex;
  align-items: center;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
}

.loan-title {
  display: block;
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
}

.loan {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem 1.15rem;
  padding-bottom: 0.5rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0;
  padding: 0;
  border: 0;
}

.field.wide,
.hint {
  grid-column: 1 / -1;
}

.field span,
.field legend {
  color: var(--mute);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0;
}

.field input,
.field select {
  width: 100%;
  min-height: 48px;
  background: #eef4f6;
  border: 0;
  border-radius: 12px;
  padding: 0.55rem 0.75rem;
  font-size: 16px;
  outline: none;
}

.field select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5' stroke='%23102227' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 1.8rem;
}

.field input:focus,
.field select:focus {
  outline: 2px solid var(--mkt-deep);
}

.seg {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
  background: #eef4f6;
  padding: 4px;
  border-radius: 12px;
}

.seg label {
  position: relative;
  min-height: 48px;
  display: grid;
  place-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 9px;
  color: var(--mute);
  font-size: 0.88rem;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
}

.seg input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.seg label:has(input:checked) {
  background: var(--well);
  color: var(--well-text);
  font-weight: 700;
}

.hint {
  margin: 0;
  color: var(--mute);
  font-size: 0.8rem;
}

.lvr-readout {
  margin-top: -0.25rem;
}

.addr-fold {
  grid-column: 1 / -1;
  margin: 0;
}

.addr-fold > summary {
  min-height: 44px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--mute);
  font-size: 0.84rem;
  font-weight: 600;
  list-style: none;
}

.addr-fold > summary::-webkit-details-marker {
  display: none;
}

.address {
  position: relative;
}

.address-list {
  position: absolute;
  z-index: 4;
  left: 0;
  right: 0;
  margin: 0.25rem 0 0;
  padding: 0.3rem;
  list-style: none;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 40px -22px rgba(16, 34, 39, 0.5);
}

.address-list button {
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 10px;
  padding: 0.65rem 0.7rem;
  font-size: 0.88rem;
}

.address-list button:hover,
.address-list button:focus-visible {
  background: #eef4f6;
}

.well,
.panel {
  margin-top: 0;
  border-radius: var(--radius);
  padding: 1.25rem 1.2rem 1.3rem;
}

.well {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  height: max-content;
  background: var(--well);
  color: var(--well-text);
  padding: 1.15rem 1.15rem 1.1rem;
}

.well-more {
  margin: 0;
  border-top: 1px solid var(--well-line);
  padding-top: 0.35rem;
}

.well-more > summary {
  min-height: 44px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--you);
  font-size: 0.92rem;
  font-weight: 700;
  list-style: none;
}

.well-more > summary::-webkit-details-marker {
  display: none;
}

.panel {
  background: var(--panel);
}

.well h2,
.panel h2,
.guide h2,
.faq h2 {
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
}

.well h2 {
  margin: 0;
  color: var(--well-mute);
  font-weight: 600;
}

.gap-hero {
  margin: 0.15rem 0 0;
}

.gap-hero span {
  display: block;
  color: var(--well-mute);
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.gap-hero b {
  display: block;
  margin: 0.2rem 0 0.15rem;
  font-family: var(--display);
  font-size: clamp(2.4rem, 8vw, 3.1rem);
  font-weight: 500;
  letter-spacing: -0.045em;
  line-height: 0.95;
  color: var(--you);
}

.gap-hero small {
  display: block;
  color: var(--well-mute);
  font-size: 0.84rem;
}

.well.is-hit .gap-hero b {
  animation: gap-punch 0.45s ease;
}

@keyframes gap-punch {
  0% {
    transform: scale(1.06);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .well.is-hit .gap-hero b {
    animation: none;
  }

  .match-beats li.is-now button {
    animation: none;
  }
}

.cite {
  display: grid;
  gap: 0.2rem;
  margin: 0;
  color: var(--well-text);
  font-size: 0.84rem;
  line-height: 1.4;
}

.cite a {
  color: var(--mkt);
  font-weight: 600;
  text-decoration: none;
}

.cite a:hover,
.cite a:focus-visible {
  text-decoration: underline;
}

.cite small {
  display: block;
  color: var(--well-mute);
  font-size: 0.75rem;
  font-weight: 500;
}

.provenance {
  margin: 0.45rem 0 0;
  color: var(--well-mute);
  font-size: 0.78rem;
}

.provenance summary {
  cursor: pointer;
  color: var(--mkt);
  font-size: 0.78rem;
}

.provenance summary span {
  display: block;
  margin-top: 0.15rem;
  color: var(--well-mute);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.provenance p {
  margin: 0.45rem 0 0;
  line-height: 1.4;
}

.provenance a {
  color: var(--mkt);
}

.app.has-dock {
  padding-bottom: calc(5.5rem + env(safe-area-inset-bottom));
}

html:has(.app.has-dock) {
  scroll-padding-bottom: calc(5.5rem + env(safe-area-inset-bottom));
}

:focus-visible {
  outline: 2px solid var(--mkt-deep);
  outline-offset: 2px;
}

.rate-warn {
  color: #8a3a1a;
  font-weight: 600;
}

.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin: 0;
}

.pair p {
  margin: 0;
}

.pair span {
  display: block;
  color: var(--well-mute);
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.pair b {
  display: flex;
  align-items: baseline;
  margin: 0.15rem 0;
  font-family: var(--display);
  font-size: 1.65rem;
  font-weight: 500;
  letter-spacing: -0.04em;
  line-height: 0.95;
}

.pair-you b {
  color: var(--you);
}

.pair-mkt b {
  color: var(--mkt);
}

.pair small {
  color: var(--well-mute);
  font-size: 0.82rem;
}

.bars {
  display: grid;
  gap: 0.4rem;
  margin: 0;
}

.bar-row {
  display: grid;
  grid-template-columns: 3.4rem 1fr auto;
  gap: 0.55rem;
  align-items: center;
}

.bar-row span {
  color: var(--well-mute);
  font-size: 0.72rem;
}

.bar-row b {
  font-variant-numeric: tabular-nums;
  font-size: 0.85rem;
}

.track {
  height: 14px;
  background: var(--well-line);
  border-radius: 99px;
  overflow: hidden;
}

.track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  transition: width 0.35s ease;
}

.track i.you {
  background: var(--you);
}

.track i.mkt {
  background: var(--mkt);
}

.leak {
  margin: 0 -1rem;
  padding: 0.7rem 1rem;
  background: #2a1a14;
  color: var(--you);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25;
}

.leak strong {
  font-variant-numeric: tabular-nums;
  color: #fff;
}

.next-line {
  margin: 0 0 0.85rem;
  color: var(--well-mute);
  font-size: 0.88rem;
  font-weight: 500;
}

.plot {
  margin: 0;
}

.well .plot {
  padding: 0.75rem 0.85rem 0.65rem;
  background: #0e272d;
  border: 1px solid var(--well-line);
  border-radius: 18px;
}

.plot svg {
  display: block;
  width: 100%;
  height: auto;
  min-height: 0;
  touch-action: none;
}

.plot-strip svg {
  max-height: 56px;
  aspect-ratio: 320 / 56;
}

.plot-scrub svg {
  cursor: ew-resize;
  max-height: 100px;
  aspect-ratio: 320 / 100;
}

.plot-axis,
.plot-tick {
  stroke: var(--well-line);
  stroke-width: 2;
  fill: none;
}

.plot-grid {
  stroke: var(--well-line);
  stroke-width: 1;
  fill: none;
}

.plot-play {
  stroke: #fff;
  stroke-width: 1.5;
  stroke-dasharray: 3 4;
  fill: none;
}

.plot-dash {
  stroke: var(--well-mute);
  stroke-width: 1.5;
  stroke-dasharray: 5 4;
  fill: none;
}

.plot-axis-label,
.plot-call {
  fill: var(--well-mute);
  font-family: var(--ui);
  font-size: 10px;
}

.plot-call {
  fill: var(--well-text);
  font-size: 11px;
  font-weight: 700;
}

.plot-call.you {
  fill: var(--you);
}

.plot-you {
  fill: var(--you);
}

.plot-you-dot {
  fill: var(--you);
  stroke: var(--well);
  stroke-width: 3;
}

.plot-mkt {
  fill: var(--mkt);
}

.plot-mkt.is-on {
  fill: var(--well);
  stroke: var(--mkt);
  stroke-width: 3;
}

.plot-mkt-dot {
  fill: var(--mkt);
  stroke: var(--well);
  stroke-width: 3;
}

.plot-hit {
  fill: transparent;
  stroke: none;
  cursor: pointer;
}

.plot-line {
  fill: none;
  stroke-width: 3;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.plot-line.you {
  stroke: var(--you);
}

.plot-line.mkt {
  stroke: var(--mkt);
}

.plot-fill.you {
  fill: var(--you);
  fill-opacity: 0.35;
}

.plot-fill.mkt {
  fill: var(--mkt);
  fill-opacity: 0.28;
}

.plot-readout {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.45rem 0.65rem;
  margin: 0 0 0.25rem;
}

.plot-readout span,
.plot-readout em {
  color: var(--well-mute);
  font-size: 0.78rem;
  font-style: normal;
}

.plot-readout strong {
  font-family: var(--display);
  font-size: 1.35rem;
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--you);
}

.plot figcaption {
  margin: 0.45rem 0 0;
  color: var(--well-mute);
  font-size: 0.75rem;
}

.panel .plot-readout strong {
  color: var(--ink);
}

.panel .plot-readout span,
.panel .plot-readout em,
.panel .plot figcaption,
.panel .plot-axis-label {
  color: var(--mute);
  fill: var(--mute);
}

.panel .plot-axis,
.panel .plot-grid {
  stroke: #d3dee2;
}

.panel .plot-play {
  stroke: var(--ink);
}

.note {
  margin: 0 0 0.9rem;
  color: var(--mute);
  font-size: 0.92rem;
}

.products {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.45rem;
}

.products.is-cited {
  gap: 0.55rem;
}

.product {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.1rem 0.75rem;
  text-align: left;
  background: #eef4f6;
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 0.8rem 0.9rem;
  min-height: 56px;
}

.product.is-teaser {
  cursor: pointer;
}

.product.is-on,
.product[aria-pressed="true"] {
  border-color: var(--mkt-deep);
  background: #e7f4f2;
  box-shadow: inset 4px 0 0 var(--mkt-deep);
}

.pack-gate {
  margin: 0.85rem 0 0;
  padding-top: 0.9rem;
  border-top: 1px solid #c9d6dc;
}

.pack-gate h3 {
  margin: 0 0 0.3rem;
  font-size: 1.02rem;
}

.who {
  font-weight: 700;
}

.who small {
  display: block;
  color: var(--mute);
  font-weight: 500;
  font-size: 0.82rem;
}

.pct {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: var(--mkt-deep);
}

.meta {
  grid-column: 1 / -1;
  color: var(--mute);
  font-size: 0.78rem;
}

.print {
  grid-column: 1 / -1;
  color: var(--mute);
  font-size: 0.72rem;
  line-height: 1.4;
  font-weight: 500;
}

.print-note {
  margin: 0.7rem 0 0;
  color: var(--mute);
  font-size: 0.78rem;
  line-height: 1.45;
}

.tune {
  margin: 0.7rem 0 0.85rem;
  background: #eef4f6;
  border-radius: 16px;
  padding: 0.55rem 0.85rem;
}

.tune summary {
  cursor: pointer;
  font-weight: 700;
  font-size: 0.88rem;
}

.tune-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 1rem;
  margin-top: 0.65rem;
}

.tune-grid label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
}

.source-fold {
  margin-top: 0;
}

.source-fold > summary {
  list-style: none;
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  min-height: 48px;
  cursor: pointer;
  border-bottom: 1px solid #c9d6dc;
}

.source-fold > summary::-webkit-details-marker {
  display: none;
}

.source-fold > summary::after {
  content: "+";
  color: var(--mkt-deep);
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1;
}

.source-fold[open] > summary::after {
  content: "–";
}

.source-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mute);
}

.source-fold strong {
  font-size: 0.98rem;
}

.source-hint {
  margin-left: auto;
  color: var(--mute);
  font-size: 0.8rem;
  font-weight: 600;
}

.source-fold[open] {
  background: var(--panel);
  border-radius: var(--radius);
  padding: 0.2rem 1.1rem 1.05rem;
}

.source-fold[open] > summary {
  margin-bottom: 0.35rem;
  border-bottom: 0;
}

.panel-search {
  margin: 0.85rem 0 0.7rem;
}

.panel-cats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0 0 0.85rem;
}

.panel-cats button {
  min-height: 36px;
  border: 0;
  border-radius: 999px;
  background: #eef4f6;
  padding: 0.35rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--mute);
}

.panel-cats button[aria-selected="true"] {
  background: var(--well);
  color: var(--well-text);
}

.source-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.45rem;
}

.source-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  min-height: 52px;
  background: #eef4f6;
  border-radius: 14px;
  padding: 0.55rem 0.85rem;
  font-size: 0.92rem;
}

.source-list a {
  flex: 0 0 auto;
  border-radius: 999px;
  background: var(--panel);
  padding: 0.3rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;
}

.app.is-compact .product {
  padding: 0.55rem 0.7rem;
  min-height: 0;
}

.beats {
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0;
  display: grid;
  gap: 0.45rem;
}

.beats li {
  background: var(--pad);
  border-radius: 14px;
}

.beats summary {
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 0.55rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
}

.beats summary::-webkit-details-marker {
  display: none;
}

.beat-hint {
  margin: 0 0.85rem 0.35rem;
  color: var(--mute);
  font-size: 0.75rem;
}

.beat-say {
  margin: 0;
  padding: 0 0.85rem 0.8rem;
  font-family: var(--mono);
  font-size: 0.8rem;
  line-height: 1.5;
}

.well-actions {
  display: none;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  margin: 0;
}

.well-actions button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.12rem;
  min-height: 58px;
  text-align: left;
  background: color-mix(in srgb, var(--mkt) 18%, #10282e);
  border: 0;
  border-radius: 16px;
  padding: 0.55rem 0.8rem 0.55rem 0.9rem;
  color: var(--mkt);
  font-weight: 700;
  line-height: 1.15;
  box-shadow: inset 4px 0 0 var(--mkt);
}

.well-actions button small {
  display: block;
  color: var(--well-mute);
  font-weight: 500;
  font-size: 0.72rem;
  line-height: 1.25;
}

.well-actions .is-stay {
  background: color-mix(in srgb, var(--you) 20%, #10282e);
  color: var(--you);
  box-shadow: inset 4px 0 0 var(--you);
}

.well-actions button[aria-pressed="true"] {
  background: color-mix(in srgb, var(--mkt) 28%, #10282e);
  box-shadow: inset 4px 0 0 var(--mkt), inset 0 0 0 1px var(--mkt);
}

.well-actions .is-stay[aria-pressed="true"] {
  background: color-mix(in srgb, var(--you) 28%, #10282e);
  box-shadow: inset 4px 0 0 var(--you), inset 0 0 0 1px var(--you);
}

.well-empty-copy {
  margin: 1.4rem 0 0.4rem;
  color: var(--well-mute);
  font-size: 1.05rem;
  line-height: 1.4;
}

.next-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

.next-head h2 {
  margin: 0;
}

.next-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.3rem;
  background: #eef4f6;
  padding: 3px;
  border-radius: 12px;
  min-width: 10.5rem;
}

.next-tabs button {
  min-height: 36px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--mute);
  font-weight: 700;
  font-size: 0.82rem;
}

.next-tabs button[aria-selected="true"] {
  background: var(--well);
  color: var(--well-text);
}

.script-mail {
  margin-top: 0.85rem;
}

.script-mail summary {
  min-height: 44px;
  font-weight: 700;
  cursor: pointer;
}

.copy,
.ask,
a.copy {
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 52px;
  margin-top: 0.85rem;
  border: 0;
  border-radius: 14px;
  background: var(--well);
  color: var(--well-text);
  font-weight: 700;
  text-decoration: none;
  text-align: center;
}

.ask:disabled {
  opacity: 0.65;
  cursor: wait;
}

.form-error {
  margin: 0.55rem 0 0;
  color: #8a2f1c;
  font-size: 0.88rem;
  font-weight: 600;
}

.ghost {
  width: 100%;
  min-height: 48px;
  margin-top: 0.65rem;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: var(--mkt-deep);
  font-weight: 700;
}

.lead {
  display: grid;
  gap: 0.85rem;
  margin-top: 0.75rem;
}

.consent {
  display: grid;
  grid-template-columns: 1.1rem 1fr;
  gap: 0.55rem;
  align-items: start;
  color: var(--mute);
  font-size: 0.78rem;
  line-height: 1.4;
}

.consent input {
  margin-top: 0.15rem;
}

.brief {
  margin: 1.35rem 0 0;
  background: var(--panel);
  border-radius: var(--radius);
  padding: 1.35rem 1.45rem 1.6rem;
}

.brief h2 {
  margin: 1.25rem 0 0.45rem;
  font-family: var(--display);
  font-size: 1.35rem;
  font-weight: 500;
  letter-spacing: -0.03em;
}

.brief h2:first-child {
  margin-top: 0;
}

.brief p,
.brief li {
  color: var(--mute);
}

.brief a {
  color: var(--mkt-deep);
}

.brief-list,
.brief ol {
  margin: 0;
  padding-left: 1.15rem;
}

.brief li + li {
  margin-top: 0.45rem;
}

.hub-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: 1.1rem 0 0.35rem;
}

.hub-links a {
  background: var(--page);
  color: var(--ink);
  border-radius: 999px;
  padding: 0.4rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
}

.brief .faq {
  margin-top: 1.15rem;
}

.learn-fold {
  margin-top: 1.5rem;
}

.learn-fold > summary {
  list-style: none;
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  min-height: 48px;
  cursor: pointer;
  border-bottom: 1px solid #c9d6dc;
}

.learn-fold > summary::-webkit-details-marker {
  display: none;
}

.learn-fold > summary::after {
  content: "+";
  margin-left: auto;
  color: var(--mkt-deep);
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1;
}

.learn-fold[open] > summary::after {
  content: "–";
}

.learn-fold[open] {
  background: var(--panel);
  border-radius: var(--radius);
  padding: 0.2rem 1.1rem 1.15rem;
}

.learn-fold[open] > summary {
  margin-bottom: 0.2rem;
  border-bottom: 0;
}

.learn-fold .source-hint {
  display: none;
}

.learn-fold .guide > h2,
.learn-fold .faq > h2 {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.guide,
.faq {
  margin-top: 0.7rem;
}

.guide p,
.guide li,
.faq p {
  color: var(--mute);
}

.guide ol {
  margin: 0;
  padding-left: 1.15rem;
}

.guide li + li {
  margin-top: 0.65rem;
}

.split-title {
  margin: 1.25rem 0 0.5rem;
  font-size: 1.05rem;
}

.split-ways {
  margin: 0;
  padding-left: 1.15rem;
}

.split-ways li + li {
  margin-top: 0.55rem;
}

.faq details {
  background: var(--panel);
  border-radius: 14px;
  padding: 0 0.9rem;
  margin-bottom: 0.45rem;
}

.faq summary {
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
}

.faq summary::-webkit-details-marker {
  display: none;
}

.faq summary::after {
  content: "+";
  color: var(--mute);
  font-weight: 500;
}

.faq details[open] summary::after {
  content: "–";
}

.faq details p {
  margin: 0 0 0.9rem;
}

.legal {
  margin: 1.6rem 0 0;
  color: #3e5157;
  font-size: 0.78rem;
  line-height: 1.55;
}

.ver {
  margin: 0 0 0.55rem;
  color: var(--mute);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.crash {
  width: min(var(--col), calc(100% - 2.3rem));
  margin: 18vh auto 0;
}

.crash h1 {
  margin: 0 0 0.6rem;
  font-family: var(--display);
  font-size: 2.2rem;
  font-weight: 500;
}

.legal-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
}

.dock {
  position: fixed;
  left: 50%;
  bottom: 0;
  z-index: 8;
  transform: translateX(-50%);
  width: min(var(--col), 100%);
  display: grid;
  grid-template-columns: 1fr 1.15fr 1fr;
  gap: 0.35rem;
  background: var(--page);
  padding: 0.55rem 0.85rem calc(0.55rem + env(safe-area-inset-bottom));
}

.dock button {
  min-height: 54px;
  text-align: left;
  background: var(--panel);
  border: 0;
  border-radius: 16px;
  padding: 0.45rem 0.8rem;
}

.dock button small {
  display: block;
  color: var(--mute);
  font-weight: 500;
  font-size: 0.72rem;
}

.dock .is-deal {
  box-shadow: inset 3px 0 0 var(--you);
}

.dock .is-stay {
  box-shadow: inset 3px 0 0 var(--mkt-deep);
}

.dock button[aria-pressed="true"] {
  background: var(--well);
  color: var(--well-text);
}

.dock button[aria-pressed="true"] small {
  color: var(--well-mute);
}

@media (min-width: 980px) {
  .shell {
    padding-bottom: 2.5rem;
  }

  .mast {
    grid-template-columns: 1fr minmax(16rem, 20rem);
    align-items: end;
    gap: 1.25rem 2rem;
    padding: 1.35rem 0 1.15rem;
  }

  .mast h1 {
    font-size: 3.35rem;
  }

  .promises {
    flex-direction: column;
    align-items: stretch;
    padding-bottom: 0.35rem;
  }

  .promises li {
    font-size: 0.92rem;
  }

  .well-actions {
    display: grid;
  }

  .stage {
    display: block;
  }

  .stage-rail {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(24rem, 28rem);
    align-items: start;
    column-gap: 1.5rem;
    row-gap: 1rem;
  }

  .stage-main {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: start;
    min-width: 0;
  }

  .stage-side {
    display: flex;
    flex-direction: column;
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
  }

  .stage-end {
    display: flex;
    flex-direction: column;
    grid-column: 2;
    grid-row: 2;
    min-width: 0;
  }

  .loan-fold,
  .well,
  .well-empty,
  .products-panel,
  .next,
  .source-fold {
    order: unset;
  }

  .well,
  .well-empty,
  .products-panel,
  .next,
  .source-fold {
    margin-top: 0;
  }

  .loan-fold {
    margin-bottom: 0;
    padding: 1.15rem 1.35rem 1.35rem;
  }

  .loan-fold summary {
    display: none;
  }

  .loan-title {
    font-size: 0.95rem;
  }

  .products.is-cited {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .lower,
  .guide,
  .faq,
  .legal {
    max-width: none;
  }

  .desk-split {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-top: 0.85rem;
  }

  .source-list {
    grid-template-columns: 1fr 1fr;
    gap: 0.55rem 0.7rem;
  }

  .dock {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation: none !important;
    transition: none !important;
  }
}
